import { useRef, useEffect, useState, type ReactNode, type CSSProperties } from "react";

interface FadeInProps {
  children: ReactNode;
  /** Delay in ms before animation starts (for stagger effects) */
  delay?: number;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

/**
 * Unified scroll-reveal wrapper.
 * Ultra-subtle fade-in + tiny slide-up (8px). Triggers once when ~15% visible.
 */
const FadeIn = ({ children, delay = 0, className = "", as: Tag = "div" }: FadeInProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Immediate check for elements already in viewport
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.85 && rect.bottom > 0) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -5% 0px", threshold: 0 }
    );

    observer.observe(el);

    // Failsafe: never leave content invisible if the observer never fires
    const failsafe = window.setTimeout(() => setVisible(true), 2000);

    return () => {
      observer.disconnect();
      window.clearTimeout(failsafe);
    };
  }, []);

  const style: CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(8px)",
    transition: `opacity 0.5s ease-out ${delay}ms, transform 0.5s ease-out ${delay}ms`,
  };

  return (
    // @ts-ignore – dynamic tag
    <Tag ref={ref} className={className} style={style}>
      {children}
    </Tag>
  );
};

export default FadeIn;
